export const venusTree = {
  id: 'venus',
  label: 'Venus',
  type: 'Terrestrial planet',
  summary: 'Earth\'s "evil twin": similar in size, with a runaway greenhouse atmosphere and a surface hot enough to melt lead.',
  payload: [
    {
      type: 'description',
      text: 'Venus is nearly the same size and mass as Earth but has taken a radically different path. A thick CO₂ atmosphere traps heat so efficiently that the surface averages 464 °C, hotter than Mercury, despite being almost twice as far from the Sun. It rotates backwards, and one turn on its axis (243 Earth days) takes longer than its 225-day year, and it is perpetually shrouded in yellow sulfuric-acid clouds.'
    },
    {
      type: 'stats',
      label: 'At a glance',
      entries: [
        { label: 'Diameter',          value: '12,104 km (0.95 × Earth)' },
        { label: 'Mass',              value: '4.867 × 10²⁴ kg (0.815 × Earth)' },
        { label: 'Distance from Sun', value: '108.2 M km (0.72 AU)' },
        { label: 'Orbital period',    value: '225 Earth days' },
        { label: 'Day length',        value: '243 Earth days (retrograde)' },
        { label: 'Solar day',         value: '117 Earth days' },
        { label: 'Moons',             value: '0' },
        { label: 'Surface temp',      value: '464 °C (mean)' },
        { label: 'Surface pressure',  value: '92 bar (92 × Earth)' }
      ]
    }
  ],
  children: [
    {
      id: 'vital-stats',
      label: 'Vital statistics',
      summary: 'Size, density, gravity',
      payload: [
        {
          type: 'stats',
          label: 'Physical',
          entries: [
            { label: 'Equatorial radius', value: '6,051.8 km' },
            { label: 'Mean density',      value: '5.243 g/cm³' },
            { label: 'Surface gravity',   value: '8.87 m/s² (0.904 g)' },
            { label: 'Escape velocity',   value: '10.36 km/s' },
            { label: 'Axial tilt',        value: '177.36° (nearly flipped)' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'Venus has no detectable intrinsic magnetic field. Its core may be entirely solid, or its slow rotation may be too slow to drive a dynamo. The solar wind interacts directly with the upper atmosphere instead.'
        }
      ]
    },
    {
      id: 'atmosphere',
      label: 'Atmosphere',
      summary: 'CO₂-dominated, 92-bar surface pressure, sulfuric-acid clouds',
      payload: [
        {
          type: 'description',
          text: 'Venus\' surface pressure is about 92 times Earth\'s, like being some 900 m underwater, and the air at the surface is about 50 times denser than Earth\'s. Composed almost entirely of carbon dioxide, it drives the most extreme greenhouse effect in the solar system. High-altitude clouds of sulfuric acid reflect about three quarters of incoming sunlight (Bond albedo 0.77), yet the surface still cooks.'
        },
        {
          type: 'composition-bars',
          entries: [
            { name: 'Carbon dioxide (CO₂)', percent: 96.5, color: '#ff9466' },
            { name: 'Nitrogen (N₂)',        percent: 3.5,  color: '#4a9eff' },
            { name: 'Sulfur dioxide (SO₂)', percent: 0.015, color: '#ffd966' },
            { name: 'Argon (Ar)',           percent: 0.007, color: '#c099ff' },
            { name: 'Water vapor (H₂O)',    percent: 0.002, color: '#88ccff' }
          ]
        },
        {
          type: 'stats',
          label: 'Clouds',
          entries: [
            { label: 'Cloud altitude',  value: '48–70 km' },
            { label: 'Composition',     value: 'H₂SO₄ droplets' },
            { label: 'Super-rotation',  value: '~360 km/h (60 × planet\'s spin)' },
            { label: 'Lightning',       value: 'Debated: radio hints, few optical flashes' }
          ]
        }
      ]
    },
    {
      id: 'surface',
      label: 'Surface & geology',
      summary: 'Volcanic plains, coronae and tesserae on a young, active surface',
      payload: [
        {
          type: 'description',
          text: 'About 80% of Venus is covered by smooth volcanic plains. The rest is two "continents": Ishtar Terra in the north and Aphrodite Terra near the equator. Surface craters are sparse and mostly young, suggesting a global resurfacing event ~300–600 million years ago. Recent radar analysis of Magellan data has found evidence of ongoing volcanic activity.'
        },
        {
          type: 'stats',
          label: 'Notable features',
          entries: [
            { label: 'Maxwell Montes',  value: '11 km, the tallest mountain' },
            { label: 'Maat Mons',       value: '8 km volcano, may be active' },
            { label: 'Coronae',         value: 'Circular volcanic features, many' },
            { label: 'Tesserae',        value: 'Deformed highland terrain' }
          ]
        },
        {
          type: 'callout',
          tone: 'warn',
          text: 'The Soviet Venera probes landed in the 1970s and 80s. The longest survivor lasted about two hours (127 minutes) on the surface; the shortest, 23 minutes.'
        }
      ]
    },
    {
      id: 'runaway-greenhouse',
      label: 'Runaway greenhouse',
      summary: 'How Venus got here, and what it tells us about Earth',
      payload: [
        { type: 'description', text: 'Early Venus may have had liquid water oceans for up to 2 billion years. As the Sun brightened, surface water evaporated, and water vapor, a potent greenhouse gas, amplified the warming. Hydrogen from photodissociated water then escaped to space. Without oceans, carbon that would normally be locked in carbonate rocks stayed in the atmosphere as CO₂. The result: a self-reinforcing runaway.' },
        { type: 'callout', tone: 'info', text: 'Studying Venus is, in part, studying a worst-case climate scenario. Earth is in no danger of going full Venus. Venus still shows how feedbacks can lock a planet into a new state.' }
      ]
    },
    {
      id: 'missions',
      label: 'Missions & exploration',
      summary: 'From Venera to DAVINCI and VERITAS',
      payload: [
        {
          type: 'stats',
          entries: [
            { label: 'Venera 1–16',   value: '1961–1983, Soviet landers & orbiters' },
            { label: 'Mariner 2',     value: '1962, the first successful planetary flyby' },
            { label: 'Magellan',      value: '1990–1994, full radar map' },
            { label: 'Venus Express', value: '2006–2014, ESA orbiter' },
            { label: 'Akatsuki',      value: '2015–2024, JAXA atmospheric orbiter (ended Sept 2025)' },
            { label: 'DAVINCI',       value: 'NASA atmospheric probe; funded for 2026 after a proposed cancellation' },
            { label: 'VERITAS',       value: 'NASA radar orbiter, no earlier than 2031; funded for 2026 after a proposed cancellation' },
            { label: 'EnVision',      value: 'ESA, planned launch November 2031' }
          ]
        },
        {
          type: 'callout',
          tone: 'warn',
          text: 'The US budget request for fiscal 2026 proposed cancelling DAVINCI and VERITAS. Congress kept both: the 2026 funding law, signed on 23 January 2026, gives DAVINCI $99.0 million and VERITAS $36.1 million for the year. The fiscal 2027 request (April 2026) proposes cancelling them again, along with NASA\'s radar for ESA\'s EnVision, and the House\'s 2027 bill does not protect them by name. As of October 2026, NASA runs on a stopgap at 2026 rates until 11 December 2026, and Congress has not yet settled 2027 funding.'
        }
      ]
    },
    {
      id: 'references',
      label: 'External references',
      summary: 'Where to dig deeper',
      payload: [
        {
          type: 'links',
          entries: [
            { label: 'NASA fact sheet',  url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/venusfact.html' },
            { label: 'Magellan archive', url: 'https://nssdc.gsfc.nasa.gov/planetary/magellan.html' },
            { label: 'Akatsuki',         url: 'https://akatsuki.isas.jaxa.jp/en/' },
            { label: 'Akatsuki operations end (JAXA)', url: 'https://global.jaxa.jp/press/2025/09/20250918-2_e.html' },
            { label: 'NASA: Venus facts', url: 'https://science.nasa.gov/venus/venus-facts/' },
            { label: 'Whistlers without lightning (GRL 2023)', url: 'https://doi.org/10.1029/2023GL105426' },
            { label: 'Planetary Society: the FY2026 NASA budget (Jan 2026)', url: 'https://www.planetary.org/articles/advocacy-success-fy2026-nasa-budget' },
            { label: 'Planetary Society: the FY2027 budget request (Apr 2026)', url: 'https://www.planetary.org/articles/analyzing-the-fy-2027-nasa-budget-request' },
            { label: 'Planetary Society: House FY2027 bill (May 2026)', url: 'https://www.planetary.org/articles/house-appropriators-advance-key-nasa-funding-bill' },
            { label: 'SpacePolicyOnline: FY2027 stopgap signed (Sept 2026)', url: 'https://spacepolicyonline.com/news/house-clears-fy2027-cr-now-to-the-president/' },
            { label: 'Wikipedia: Venus', url: 'https://en.wikipedia.org/wiki/Venus' }
          ]
        }
      ]
    }
  ]
};
